import React from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Cancel from '@mui/icons-material/Cancel';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DisabledByDefaultIcon from '@mui/icons-material/DisabledByDefault';

export type CheckSelectValue<Multiple> = Multiple extends undefined | false
  ? string
  : Array<string>;

interface Props<Multiple extends boolean | undefined> {
  choices: Record<string, string>;
  sx?: React.CSSProperties;
  className?: string;
  label?: string | React.ReactNode;
  subLabel?: string;
  value: CheckSelectValue<Multiple>;
  onChange: (value: any) => void;
  checkedBoxSx?: React.CSSProperties;
  multiple?: Multiple;
  negative?: boolean;
  expendChildren?: Record<string, React.ReactNode>;
  isChild?: boolean;
  error?: boolean;
  helperText?: string;
}

const CheckBoxChoose = <Multiple extends boolean | undefined = undefined>(
  props: Props<Multiple>,
) => {
  const {
    sx,
    className,
    label,
    subLabel,
    choices = {},
    value,
    onChange,
    checkedBoxSx = {},
    multiple,
    negative = false,
    expendChildren,
    isChild,
    error,
    helperText,
  } = props;

  const handleChange = (choice: string) => {
    let newValue: any = choice;
    if (multiple) {
      newValue = Array.isArray(value) ? value.slice() : [];
      if (value.includes(choice)) {
        newValue.splice(newValue.indexOf(choice), 1);
      } else {
        newValue.push(choice);
      }
    }
    onChange(newValue);
  };

  const isLastChild = (index: number) => {
    const lastIndex = Array.isArray(choices)
      ? choices.length
      : Object.keys(choices).length;
    return isChild && index === lastIndex - 1;
  };

  const renderOption = (label: string, inputValue: string, key: number) => {
    return (
      <div
        key={key}
        className={`bg-slate-50 px-6 rounded ${
          isLastChild(key) ? 'py-0' : 'py-4'
        }`}
      >
        <Checkbox
          checked={multiple ? value.includes(inputValue) : value === inputValue}
          onChange={() => handleChange(inputValue)}
          inputProps={{ 'aria-label': 'controlled' }}
          checkedIcon={
            multiple ? (
              negative ? (
                <DisabledByDefaultIcon />
              ) : (
                <CheckBoxIcon />
              )
            ) : negative ? (
              <Cancel />
            ) : (
              <CheckCircleRounded />
            )
          }
          icon={
            multiple ? <CheckBoxOutlineBlankIcon /> : <RadioButtonUnchecked />
          }
          sx={{
            padding: 0,
            '&.Mui-checked': {
              color: negative ? '#dc2626' : '#0050b3',
            },
            '&:hover': {
              color: negative ? '#dc2626' : '#0050b3',
            },
            ...checkedBoxSx,
          }}
        />
        <p className="inline-block text-sm leading-5.5 ml-4 text-black">
          {label}
        </p>
        {value === inputValue &&
          expendChildren &&
          expendChildren[value as string]}
      </div>
    );
  };

  const renderOptions = () => {
    return (
      <div className="flex flex-col gap-3 mt-3">
        {Object.keys(choices).map((key, index) =>
          renderOption(choices[key], key, index),
        )}
      </div>
    );
  };

  return (
    <>
      <Box className={`flex flex-col ${className}`} sx={sx}>
        <div className="mb-3">
          {label && (
            <label className="text-sm font-semibold mb-1">{label}</label>
          )}
          {subLabel && (
            <p className="text-xs leading-5 text-black/60">{subLabel}</p>
          )}
          {renderOptions()}
        </div>
      </Box>
      {error && helperText && (
        <span className="text-red-700 text-xs mt-2">{helperText}</span>
      )}
    </>
  );
};

export default CheckBoxChoose;
